import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

const TABS = [
  { id: "shipping", label: "Shipping Policy",    icon: "📦" },
  { id: "returns",  label: "Return & Refund",     icon: "↩" },
  { id: "privacy",  label: "Privacy Policy",      icon: "🔒" },
  { id: "terms",    label: "Terms & Conditions",  icon: "📄" },
];

export default function PoliciesPage() {
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const validIds = TABS.map(t => t.id);
  const [activeTab, setActiveTab] = useState(validIds.includes(tabFromUrl) ? tabFromUrl : "shipping");

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (validIds.includes(tab)) setActiveTab(tab);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [searchParams]); // eslint-disable-line

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <div className="flex-1 px-6 md:px-12 max-w-5xl mx-auto py-16 md:py-24 w-full">

        <h1 className="cormorant text-5xl font-light mb-2" data-testid="policies-title">Policies</h1>
        <p className="text-sm text-zinc-500 mb-10">Everything you need to know about shopping with Rivora Jewels.</p>

        {/* ── Tab nav ───────────────────────────────────────────────── */}
        <div className="flex flex-wrap gap-2 mb-10 border-b border-zinc-200 pb-4">
          {TABS.map(({ id, label, icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 text-xs uppercase tracking-widest px-4 py-2.5 transition-colors rounded-sm ${
                activeTab === id
                  ? "text-white"
                  : "text-zinc-500 hover:text-zinc-900"
              }`}
              style={activeTab === id ? { backgroundColor: "#C9A227" } : {}}
            >
              <span>{icon}</span>
              {label}
            </button>
          ))}
        </div>

        {/* ── Shipping ─────────────────────────────────────────────── */}
        {activeTab === "shipping" && (
          <div className="space-y-5 text-zinc-600 text-sm" style={{ lineHeight: "1.85" }}>
            <h2 className="cormorant text-3xl font-light text-zinc-900 mb-6">Shipping Policy</h2>

            <p>Every Rivora order is carefully packed in our signature packaging and dispatched within <strong className="text-zinc-800">1–2 business days</strong>. We offer <strong className="text-zinc-800">free shipping across India</strong> on all orders — no minimum required.</p>

            <p>Estimated delivery time is <strong className="text-zinc-800">5–7 business days</strong> depending on your location. Once your order is dispatched, you'll receive a tracking link via email and SMS.</p>

            <p>For remote or hard-to-reach pincodes, delivery may take up to 10 business days. If your order hasn't arrived within the estimated window, please reach out and we'll look into it right away.</p>

            {/* Reassurance box */}
            <div className="mt-8 border border-zinc-100 bg-[#FAFAF8] rounded-sm p-5">
              <p className="text-xs uppercase tracking-widest text-zinc-400 mb-3">Need help with your order?</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="https://wa.me/919999999999?text=Hi%20Rivora!%20I%20need%20help%20with%20my%20order."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-zinc-700 hover:text-[#1D7F5B] transition-colors"
                >
                  <span>💬</span> WhatsApp Support
                </a>
                <a
                  href="mailto:hello@rivorajewels.com"
                  className="flex items-center gap-2 text-sm text-zinc-700 hover:text-zinc-900 transition-colors"
                >
                  <span>✉</span> rivorajewels.business@gmail.com
                </a>
              </div>
              <p className="text-xs text-zinc-400 mt-3">Response within 24 hours on business days.</p>
            </div>
          </div>
        )}

        {/* ── Return & Refund ──────────────────────────────────────── */}
        {activeTab === "returns" && (
          <div className="space-y-6 text-zinc-600 text-sm" style={{ lineHeight: "1.85" }}>
            <h2 className="cormorant text-3xl font-light text-zinc-900 mb-2">Return & Refund Policy</h2>

            <p>At Rivora, every piece is carefully inspected before dispatch to ensure it reaches you in perfect condition. Since jewellery is a personal-use product, <strong className="text-zinc-800">we do not offer returns or refunds for reasons such as change of mind, incorrect size selection, or personal preference.</strong></p>

            <div>
              <p className="font-medium text-zinc-800 mb-3">We will gladly assist if:</p>
              <ul className="space-y-2 pl-2">
                {["You receive a damaged product", "You receive the wrong item"].map(item => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-[#C9A227] mt-0.5">✓</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-l-2 border-[#C9A96E] pl-5 py-2 bg-amber-50/40 rounded-r-sm">
              <p className="font-medium text-zinc-800 mb-2">To be eligible:</p>
              <ul className="space-y-2">
                {[
                  "Contact us within 24 hours of delivery.",
                  "Share a continuous unboxing video starting before opening the sealed package.",
                  "The video must clearly show the shipping label and the complete unboxing process — without cuts or edits.",
                  "The product must remain unused.",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-[#C9A96E] font-medium flex-shrink-0">{i + 1}.</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-3 text-zinc-700 font-medium">Once verified, we'll arrange a replacement or refund where applicable.</p>
              <p className="mt-1 text-zinc-500">Claims submitted without a valid unboxing video cannot be processed.</p>
            </div>

            <div>
              <p className="font-medium text-zinc-800 mb-2">Not eligible for return:</p>
              <ul className="space-y-1.5 pl-2">
                {[
                  "Change of mind or personal preference",
                  "Incorrect size selected by the customer",
                  "Normal variations in handcrafted jewellery",
                  "Minor colour differences due to lighting or screen settings",
                  "Products damaged after delivery",
                  "Claims without an unboxing video",
                  "Customised or personalised jewellery",
                ].map(item => (
                  <li key={item} className="flex items-start gap-2">
                    <span className="text-zinc-300 mt-0.5">—</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-zinc-50 border border-zinc-100 p-4 rounded-sm">
              <p className="font-medium text-zinc-800 mb-1">Refund Timeline</p>
              <p>Approved refunds are processed within <strong className="text-zinc-800">5–7 business days</strong> and credited to your original payment method.</p>
            </div>
          </div>
        )}

        {/* ── Privacy Policy ───────────────────────────────────────── */}
        {activeTab === "privacy" && (
          <div className="space-y-8 text-zinc-600 text-sm" style={{ lineHeight: "1.85" }}>
            <h2 className="cormorant text-3xl font-light text-zinc-900 mb-2">Privacy Policy</h2>
            <p className="text-zinc-500">Last updated: July 2026</p>

            {[
              {
                heading: "Information We Collect",
                points: [
                  "Name, email address, phone number and delivery address when you place an order.",
                  "Payment details — processed securely by Razorpay. We never store card information.",
                  "Browsing behaviour on our site to improve your experience.",
                ],
              },
              {
                heading: "How We Use It",
                points: [
                  "To process and fulfil your orders.",
                  "To send order confirmation, tracking updates, and support communication.",
                  "To improve our website, products, and services.",
                  "To send occasional offers — only if you've opted in.",
                ],
              },
              {
                heading: "Payment Security",
                points: [
                  "All payments are processed via Razorpay, which is PCI-DSS compliant.",
                  "We do not store any payment card details on our servers.",
                ],
              },
              {
                heading: "Third-Party Services",
                points: [
                  "We share your delivery address with courier partners solely for order fulfilment.",
                  "We do not sell, rent, or trade your personal information to any third party.",
                ],
              },
              {
                heading: "Contact",
                points: [
                  "For any privacy concerns, email us at rivorajewels.business@gmail.com or reach us on WhatsApp.",
                ],
              },
            ].map(({ heading, points }) => (
              <div key={heading}>
                <p className="font-medium text-zinc-800 mb-2">{heading}</p>
                <ul className="space-y-1.5 pl-2">
                  {points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#C9A227] mt-0.5 flex-shrink-0">·</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {/* ── Terms & Conditions ───────────────────────────────────── */}
        {activeTab === "terms" && (
          <div className="space-y-8 text-zinc-600 text-sm" style={{ lineHeight: "1.85" }}>
            <h2 className="cormorant text-3xl font-light text-zinc-900 mb-2">Terms & Conditions</h2>
            <p className="text-zinc-500">By accessing and placing an order with Rivora Jewels, you agree to the following terms.</p>

            {[
              {
                heading: "Products & Availability",
                points: [
                  "All products are subject to availability.",
                  "We reserve the right to discontinue any product at any time.",
                  "Product images are for illustration — minor variations in handcrafted pieces are natural.",
                ],
              },
              {
                heading: "Pricing",
                points: [
                  "Prices are displayed in Indian Rupees (₹) and include applicable taxes.",
                  "We reserve the right to update prices without prior notice.",
                  "In the event of a pricing error, we reserve the right to cancel the order and issue a full refund.",
                ],
              },
              {
                heading: "Orders & Payments",
                points: [
                  "Orders are confirmed only after successful payment.",
                  "We accept UPI, Cards, Net Banking, and Google Pay via Razorpay.",
                  "Cash on Delivery (COD) is not available.",
                ],
              },
              {
                heading: "Intellectual Property",
                points: [
                  "All content on this website — images, text, and design — is the property of Rivora Jewels.",
                  "Reproduction or commercial use without written permission is prohibited.",
                ],
              },
              {
                heading: "Governing Law",
                points: [
                  "These terms are governed by the laws of India.",
                  "Any disputes shall be subject to the jurisdiction of courts in Jaipur, Rajasthan.",
                ],
              },
            ].map(({ heading, points }) => (
              <div key={heading}>
                <p className="font-medium text-zinc-800 mb-2">{heading}</p>
                <ul className="space-y-1.5 pl-2">
                  {points.map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#C9A227] mt-0.5 flex-shrink-0">·</span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
}
